import { FC, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { CallIntelUtils } from '@frontend/api-call-intel';
import { NoTranscription } from '@frontend/assets';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { transcribingCallLottie } from '../assets/lotties';
import { CallInsightBaseProps } from '../types';
import { callIntelUtils, callStatusUtils } from '../utils';
import { CallEmptyState } from './empty-state/call-empty-state';
import { AnalyzingCallLoader } from './loaders/analyzing-call-loader';
import { OfficeUserName } from './user-name';

export interface CallSummaryProps extends CallInsightBaseProps {
  containerStyles?: Interpolation<Theme>;
  callFlags: ReturnType<typeof callStatusUtils.getCallFlags>;
}

export const CallSummary: FC<CallSummaryProps> = ({
  call,
  callFlags,
  callMetadata,
  containerStyles,
  isLoading,
  isDemoAccount,
}) => {
  const { t } = useTranslation('call-intel-core');
  const { isFailedCall, isProcessingCall, isSkippedCall } = callFlags;

  const callSummary = useMemo(() => {
    if (!callMetadata?.summary) return '';

    return !isDemoAccount
      ? callMetadata.summary
      : CallIntelUtils.parseTranscriptWithTokens(callMetadata.summary, {
          PERSON_NAME: call?.person?.firstName ?? '',
        });
  }, [callMetadata?.summary, isDemoAccount, call?.person?.firstName]);

  const showLoading = isProcessingCall || isLoading;

  return (
    <div css={[styles.containerStyles, containerStyles]}>
      {showLoading ? (
        <AnalyzingCallLoader lottie={transcribingCallLottie} message={t('Processing phone call...')} />
      ) : isFailedCall || isSkippedCall || !callMetadata?.summary ? (
        <CallEmptyState
          mainText={t('No Summary Available')}
          style={{
            overflow: 'auto',
          }}
          svg={NoTranscription}
          subText={t('Please review the call recording.')}
        />
      ) : (
        <div css={styles.content}>
          <Text size='medium' css={styles.officeUserName}>
            <Trans t={t}>
              Office User:{' '}
              <Text as='span' weight='bold' size='medium'>
                <OfficeUserName
                  showAvatar={false}
                  displayName={callIntelUtils.getFullName(
                    call?.officeUser?.firstName,
                    call?.officeUser?.lastName,
                    t('Unknown')
                  )}
                />
              </Text>
            </Trans>
          </Text>
          <p css={styles.summary}>
            <Text as='span' size='medium'>
              {callSummary}
            </Text>
          </p>
        </div>
      )}
    </div>
  );
};

const styles = {
  containerStyles: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
  `,
  content: css`
    align-items: baseline;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    overflow: auto;
    position: relative;
  `,
  summary: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding-right: ${theme.spacing(2)};

    &::-webkit-scrollbar {
      -webkit-appearance: none;
      width: ${theme.spacing(1)};
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.neutral30};
      border-radius: ${theme.borderRadius.small};
    }
  `,

  officeUserName: css`
    display: flex;
    gap: ${theme.spacing(0.5)};
  `,
};
